import { Link } from 'react-router-dom'
import { SignupCta } from '@/components/ui'
import { SEO } from '@/components/seo'

const caseStudies = [
  {
    slug: 'meeting-to-done',
    title: 'Meeting to Done',
    subtitle: 'Client meeting at 10am. Report by lunch.',
    metric: '3+ hours',
    metricLabel: 'saved per meeting',
    excerpt:
      'Follow a retirement planning meeting as it transforms into a transcription, file note, and a suitability report, all before lunch.',
    gradient: 'from-teal-500 to-emerald-600',
    tags: ['AI Reports', 'Meeting Capture'],
  },
  {
    slug: 'practice-growth',
    title: '80 Clients to Exit',
    subtitle: 'From lifestyle practice to 7-figure sale.',
    metric: '£2.3M',
    metricLabel: 'projected exit value',
    excerpt:
      '80 clients, 60-hour weeks, no exit plan. An illustrative projection of how an adviser could use WealthSpace to scale from £34M AUM to £88M AUM and build a practice worth selling.',
    gradient: 'from-amber-500 to-orange-600',
    tags: ['Practice Tools', 'Growth'],
  },
  {
    slug: 'ramble-to-action',
    title: 'Ramble to Action',
    subtitle: 'Speak your thoughts. Watch them happen.',
    metric: '0 typing',
    metricLabel: 'required',
    excerpt:
      'Dictate into your Apple Watch while driving home. By the time you arrive, your thoughts have become tasks, notes, and client follow-ups, all organised and ready.',
    gradient: 'from-violet-500 to-purple-600',
    tags: ['Meeting Capture', 'Dictation'],
  },
]

export default function CaseStudiesPage() {
  return (
    <>
      <SEO
        title="Case Studies | WealthSpace"
        description="Illustrative adviser scenarios showing how WealthSpace could work. See how AI-powered practice management can save time and support advisory firms."
        keywords="financial adviser scenarios, IFA software, practice management scenario, AI suitability reports"
        url="/case-studies"
      />

      {/* Hero */}
      <section className="pt-24 pb-16 px-6 text-center">
        <div className="max-w-[700px] mx-auto">
          <h1 className="font-serif text-[clamp(36px,5vw,52px)] text-slate-900 mb-4">
            Adviser scenarios
          </h1>
          <p className="text-lg text-slate-600 leading-relaxed">
            Illustrative scenarios showing how UK financial advisers could use WealthSpace to save time, grow their practices, and deliver better outcomes.
          </p>
        </div>
      </section>

      {/* Case Studies */}
      <section className="pb-20 px-6">
        <div className="max-w-[1000px] mx-auto space-y-8">
          {caseStudies.map((cs) => (
            <Link
              key={cs.slug}
              to={`/case-studies/${cs.slug}`}
              className="group flex flex-col md:flex-row bg-white border border-slate-200 rounded-xl overflow-hidden shadow-card no-underline transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5"
            >
              {/* Metric panel */}
              <div className={`bg-gradient-to-br ${cs.gradient} p-10 md:w-[280px] flex-shrink-0 flex flex-col justify-center items-center text-white text-center`}>
                <div className="text-[10px] font-semibold text-white/70 tracking-[0.15em] uppercase mb-3">Illustrative</div>
                <div className="text-4xl font-serif font-semibold mb-1">{cs.metric}</div>
                <div className="text-white/80 text-sm">{cs.metricLabel}</div>
              </div>

              {/* Content */}
              <div className="p-8 flex-1">
                <div className="flex flex-wrap gap-2 mb-3">
                  {cs.tags.map((tag) => (
                    <span key={tag} className="text-xs font-medium bg-slate-100 text-slate-600 px-2.5 py-1 rounded-full">
                      {tag}
                    </span>
                  ))}
                </div>
                <h2 className="text-2xl font-serif font-medium text-slate-900 mb-1">{cs.title}</h2>
                <p className="text-sm text-slate-500 mb-3">{cs.subtitle}</p>
                <p className="text-[15px] text-slate-600 leading-relaxed mb-4">{cs.excerpt}</p>
                <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-accent-teal">
                  Read the full story
                  <svg className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M5 12h14M12 5l7 7-7 7" />
                  </svg>
                </span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-6 bg-slate-50">
        <div className="max-w-[600px] mx-auto text-center">
          <h2 className="font-serif text-3xl text-slate-900 mb-4">
            Ready to write your own story?
          </h2>
          <p className="text-slate-600 mb-8">
            Sign up in 2 minutes and start seeing results from day one.
          </p>
          <SignupCta location="case_studies_cta">
            Start Free Trial
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </SignupCta>
        </div>
      </section>
    </>
  )
}
